import { useI18n, useT } from "../../../shared/i18n/i18n";
import { formatRelativeTime } from "../../../shared/lib/format-relative-time";
import { skipToken, useQuery } from "@tanstack/react-query";
import { CalendarRange, ChevronDown, Loader2, Play, Plus, Sigma, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { BoardView } from "../../../entities/board/model/use-boards";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { sprintStateFor, todayIso, useSprints } from "../../../entities/sprint-planning/model/use-sprints";
import type { Sprint, SprintEstimateSource } from "../../../entities/sprint-planning/model/use-sprints";
import { trpc as trpcClient, useTRPC } from "../../../shared/api/trpc";
import { cn } from "../../../shared/lib/cn";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { formatSprintRange } from "./sprint-planning-surface";
import { SprintReportWidgets } from "./sprint-report-widgets";
import { sprintStateMeta } from "./sprint-state";

type SprintReport = Awaited<ReturnType<typeof trpcClient.sprints.report.query>>;

const estimateFieldTypes = new Set(["currency", "duration", "number", "percent", "rating", "select"]);

// Labels are screen text, so they are built per render in the reader's
// language; only the colours and keys are fixed.
const flowCategories = [
  { color: "#64748b", key: "not_started", labelKey: "sprint.toDo" },
  { color: "#f59e0b", key: "active", labelKey: "sprint.inProgress" },
  { color: "#22c55e", key: "done", labelKey: "sprint.done" },
  { color: "#16a34a", key: "closed", labelKey: "sprint.closed" },
] as const;

export function SprintReportSurface({
  board,
  fields,
  onOpenItem,
  onOpenReplay,
}: {
  board: BoardView;
  fields: WorkObjectFieldDefinition[];
  onOpenItem?: (workObjectId: string) => void;
  onOpenReplay?: (sprintId: string) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const sprints = useSprints(board.id);
  const orderedSprints = sprints.data?.sprints ?? [];
  const estimateSources = useMemo(() => sprints.data?.estimateSources ?? [], [sprints.data?.estimateSources]);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [selectedAxisId, setSelectedAxisId] = useState<string | null>(null);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);

  // Default to the running sprint, else the most recently closed one — the
  // two sprints a PM most often wants to inspect.
  const resolvedSprintId = useMemo(() => {
    if (selectedSprintId && orderedSprints.some((sprint) => sprint.id === selectedSprintId)) return selectedSprintId;
    const today = todayIso();
    const running = orderedSprints.find((sprint) => {
      const state = sprintStateFor(sprint, today);
      return state === "active" || state === "overdue";
    });
    const lastClosed = [...orderedSprints].reverse().find((sprint) => sprint.closedAt);
    return (running ?? lastClosed ?? orderedSprints[orderedSprints.length - 1])?.id ?? null;
  }, [orderedSprints, selectedSprintId]);
  const selectedSprint = orderedSprints.find((sprint) => sprint.id === resolvedSprintId) ?? null;

  const trpc = useTRPC();
  const reportQuery = useQuery(
    trpc.sprints.report.queryOptions(resolvedSprintId ? { sprintId: resolvedSprintId } : skipToken, {
      placeholderData: (previous) => previous,
    }),
  );
  const report = reportQuery.data ?? null;
  const activeAxis = report?.axes.find((axis) => axis.sourceId === selectedAxisId) ?? null;
  const isPrimaryAxis = activeAxis !== null && report?.axes[0]?.sourceId === activeAxis.sourceId;

  if (orderedSprints.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <CalendarRange className="size-8 text-muted-foreground/50" />
        <div>
          <p className="text-sm font-medium">{t("sprint.noSprintsToReport")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("sprint.createInBacklog")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pb-4 pr-1">
      <div className="flex flex-wrap items-center gap-3">
        <SprintPicker onSelect={setSelectedSprintId} selectedSprintId={resolvedSprintId} sprints={orderedSprints} />
        {selectedSprint ? <SprintHeadline sprint={selectedSprint} /> : null}
        {reportQuery.isFetching ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : null}
        <div className="ml-auto flex items-center gap-2">
          {report && report.axes.length > 0 ? (
            <div className="flex items-center gap-0.5 rounded-md bg-muted/50 p-0.5">
              <AxisChip isActive={activeAxis === null} label={t("sprint.items")} onClick={() => setSelectedAxisId(null)} />
              {report.axes.map((axis) => (
                <AxisChip
                  isActive={activeAxis?.sourceId === axis.sourceId}
                  key={axis.sourceId}
                  label={axis.label}
                  onClick={() => setSelectedAxisId(axis.sourceId)}
                />
              ))}
            </div>
          ) : null}
          {onOpenReplay ? (
            <Button
              className="text-muted-foreground"
              disabled={!selectedSprint || sprintStateFor(selectedSprint) === "planned"}
              onClick={() => {
                if (selectedSprint) onOpenReplay(selectedSprint.id);
              }}
              size="sm"
              variant="ghost"
            >
              <Play data-icon="inline-start" />
              {t("sprint.replay")}
            </Button>
          ) : null}
          <Button className="text-muted-foreground" onClick={() => setIsSourcesOpen(true)} size="sm" variant="ghost">
            <Sigma data-icon="inline-start" />
            {t("sprint.estimates")}
          </Button>
        </div>
      </div>

      {report ? (
        <>
          <div className={cn("grid grid-cols-2 gap-3", report.summary.removed > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4")}>
            {activeAxis ? (
              <>
                <StatCard hint={t("sprint.hintPlanned")} label={t("sprint.committed")} unit={activeAxis.unit} value={activeAxis.summary.committed} />
                <StatCard
                  hint={t("sprint.hintAdded")}
                  label={t("sprint.scopeAdded")}
                  tone={activeAxis.summary.added > 0 ? "amber" : undefined}
                  unit={activeAxis.unit}
                  value={activeAxis.summary.added}
                />
                {report.summary.removed > 0 ? (
                  <StatCard
                    hint={t("sprint.hintRemoved")}
                    label={t("sprint.scopeRemoved")}
                    tone="amber"
                    unit={activeAxis.unit}
                    value={activeAxis.summary.removed}
                  />
                ) : null}
                <StatCard
                  hint={percentOf(activeAxis.summary.done, activeAxis.summary.total)}
                  label={t("sprint.done")}
                  tone="emerald"
                  unit={activeAxis.unit}
                  value={activeAxis.summary.done}
                />
                <StatCard
                  hint={t("sprint.hintRemaining")}
                  label={t("sprint.remaining")}
                  unit={activeAxis.unit}
                  value={Math.round((activeAxis.summary.total - activeAxis.summary.done) * 100) / 100}
                />
              </>
            ) : (
              <>
                <StatCard hint={t("sprint.hintPlanned")} label={t("sprint.committed")} value={report.summary.committed} />
                <StatCard hint={t("sprint.hintAdded")} label={t("sprint.scopeAdded")} tone={report.summary.added > 0 ? "amber" : undefined} value={report.summary.added} />
                {report.summary.removed > 0 ? (
                  <StatCard hint={t("sprint.hintRemoved")} label={t("sprint.scopeRemoved")} tone="amber" value={report.summary.removed} />
                ) : null}
                <StatCard hint={percentOf(report.summary.done, report.summary.total)} label={t("sprint.done")} tone="emerald" value={report.summary.done} />
                {report.sprint.closedAt ? (
                  <StatCard hint={t("sprint.hintCarried")} label={t("sprint.carriedOver")} tone={report.summary.carried > 0 ? "amber" : undefined} value={report.summary.carried} />
                ) : (
                  <StatCard hint={t("sprint.hintOpen")} label={t("sprint.open")} value={report.summary.open} />
                )}
              </>
            )}
          </div>

          <SprintReportWidgets board={board} fields={fields} onOpenItem={onOpenItem} sprintId={resolvedSprintId} />

          <div className="grid min-h-0 grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
            <section className="rounded-lg border border-border/70 bg-card p-4">
              <h3 className="text-sm font-medium">{t("sprint.burndown")}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {activeAxis
                  ? t("sprint.burndownAxisHint", {
                      label: activeAxis.label.toLowerCase(),
                      unit: activeAxis.unit ? ` (${activeAxis.unit})` : "",
                    })
                  : t("sprint.burndownItemsHint")}
              </p>
              <BurndownChart points={activeAxis ? activeAxis.burndown : report.burndown} />
              {report.flow.length > 1 ? (
                <>
                  <div className="mt-4 flex items-center gap-3">
                    <h3 className="text-sm font-medium">{t("sprint.cumulativeFlow")}</h3>
                    <div className="flex items-center gap-2">
                      {flowCategories.map((category) => (
                        <span className="flex items-center gap-1 text-[0.65rem] text-muted-foreground" key={category.key}>
                          <span className="size-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                          {t(category.labelKey)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <FlowChart days={report.flow} />
                </>
              ) : null}
            </section>

            <div className="flex min-h-0 flex-col gap-3">
              <section className="rounded-lg border border-border/70 bg-card p-4">
                <h3 className="text-sm font-medium">{t("sprint.byStatus")}</h3>
                <div className="mt-3 flex flex-col gap-2">
                  {report.byStatus.map((entry) => (
                    <div className="flex items-center gap-2" key={entry.name}>
                      <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color ?? "#64748b" }} />
                      <span className="w-24 shrink-0 truncate text-xs">{entry.name}</span>
                      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted/60">
                        <div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: entry.color ?? "#64748b",
                            width: `${report.summary.total === 0 ? 0 : Math.round((entry.count / report.summary.total) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{entry.count}</span>
                    </div>
                  ))}
                  {report.byStatus.length === 0 ? <p className="text-xs text-muted-foreground">{t("sprint.sprintEmpty")}</p> : null}
                </div>
              </section>

              {report.timeInStatus.length > 0 ? (
                <section className="rounded-lg border border-border/70 bg-card p-4">
                  <h3 className="text-sm font-medium">{t("sprint.timeInStatus")}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("sprint.average")}</p>
                  <div className="mt-3 flex flex-col gap-2">
                    {report.timeInStatus.map((entry) => {
                      const maxAvg = Math.max(...report.timeInStatus.map((candidate) => candidate.avgHours), 1);
                      return (
                        <div className="flex items-center gap-2" key={entry.statusId ?? entry.name}>
                          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color ?? "#64748b" }} />
                          <span className="w-24 shrink-0 truncate text-xs">{entry.name}</span>
                          <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted/60">
                            <div
                              className="h-full rounded-full"
                              style={{ backgroundColor: entry.color ?? "#64748b", width: `${Math.max(3, Math.round((entry.avgHours / maxAvg) * 100))}%` }}
                            />
                          </div>
                          <span
                            className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
                            title={t("sprint.stayTooltip", { count: entry.itemCount, total: formatStayHours(entry.totalHours) })}
                          >
                            {formatStayHours(entry.avgHours)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {report.longestStays.length > 0 ? (
                    <>
                      <h4 className="mt-4 text-xs font-medium text-muted-foreground">{t("sprint.longestStays")}</h4>
                      <div className="mt-1.5 flex flex-col">
                        {report.longestStays.map((stay) => (
                          <button
                            className="flex items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent/50"
                            key={`${stay.workObjectId}:${stay.statusName}`}
                            onClick={() => onOpenItem?.(stay.workObjectId)}
                            type="button"
                          >
                            <span className="min-w-0 flex-1 truncate text-xs">{stay.workObjectTitle}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">{stay.statusName}</span>
                            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-amber-500">{formatStayHours(stay.hours)}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                </section>
              ) : null}

              <section className="rounded-lg border border-border/70 bg-card p-4">
                <h3 className="text-sm font-medium">{t("sprint.velocity")}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {isPrimaryAxis && activeAxis
                    ? t("sprint.velocityAxisHint", { label: activeAxis.label.toLowerCase() })
                    : t("sprint.velocityItemsHint")}
                </p>
                <VelocityChart activeSprintId={report.sprint.id} entries={report.velocity} useEstimates={isPrimaryAxis} />
              </section>

              {report.cycleTime ? (
                <section className="rounded-lg border border-border/70 bg-card p-4">
                  <h3 className="text-sm font-medium">{t("sprint.cycleTime")}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t("sprint.cycleTimeSummary", { count: report.cycleTime.sampleSize })}
                  </p>
                  <div className="mt-3 flex items-baseline gap-6">
                    <div>
                      <p className="text-lg font-semibold tabular-nums">{formatStayHours(report.cycleTime.p50Hours)}</p>
                      <p className="text-xs text-muted-foreground">{t("sprint.median")}</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold tabular-nums">{formatStayHours(report.cycleTime.p85Hours)}</p>
                      <p className="text-xs text-muted-foreground">{t("sprint.percentile85")}</p>
                    </div>
                    {report.forecast ? (
                      <div className="ml-auto text-right">
                        <p className="text-lg font-semibold tabular-nums">
                          {report.forecast.onTrack}
                          <span className="text-muted-foreground">/{report.forecast.onTrack + report.forecast.atRisk}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{t("sprint.forecastFits")}</p>
                      </div>
                    ) : null}
                  </div>
                  {report.forecast && report.forecast.atRisk > 0 ? (
                    <p className="mt-2 text-xs text-amber-500">{t("sprint.forecastRisk", { count: report.forecast.atRisk })}</p>
                  ) : null}
                </section>
              ) : null}
            </div>
          </div>

          <section className="rounded-lg border border-border/70 bg-card">
            <div className="flex items-baseline gap-2 px-4 pb-1 pt-3">
              <h3 className="text-sm font-medium">{t("sprint.items")}</h3>
              <span className="text-xs tabular-nums text-muted-foreground">{report.items.length}</span>
            </div>
            <div className="flex flex-col px-2 pb-2">
              {report.items.map((item) => (
                <button
                  className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                  key={item.workObjectId}
                  onClick={() => onOpenItem?.(item.workObjectId)}
                  type="button"
                >
                  <span
                    className={cn("size-2 shrink-0 rounded-full", item.completed ? "" : "opacity-70")}
                    style={{ backgroundColor: item.statusColor ?? "#64748b" }}
                  />
                  <span className={cn("min-w-0 flex-1 truncate text-sm", item.completed && "text-muted-foreground")}>
                    {item.workObjectTitle}
                  </span>
                  {item.typeName ? (
                    <Badge className="shrink-0 px-1.5 py-0 text-[0.65rem]" variant="secondary">
                      {item.typeName}
                    </Badge>
                  ) : null}
                  {activeAxis ? (
                    <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {item.estimates[activeAxis.sourceId] ?? 0}
                      {activeAxis.unit ?? ""}
                    </span>
                  ) : null}
                  <span className="w-24 shrink-0 truncate text-right text-xs text-muted-foreground">{item.statusName}</span>
                  {!item.committed ? (
                    <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[0.65rem] font-medium text-amber-600 dark:text-amber-400">
                      {t("sprint.added")}
                    </span>
                  ) : null}
                  {item.carryTarget ? (
                    <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[0.65rem] font-medium text-muted-foreground">
                      {t("sprint.carried")}
                    </span>
                  ) : null}
                </button>
              ))}
              {report.items.length === 0 ? <p className="px-2 py-4 text-xs text-muted-foreground">{t("sprint.nothingPlanned")}</p> : null}
            </div>
          </section>

          {report.removedItems.length > 0 ? (
            <section className="rounded-lg border border-border/70 bg-card">
              <div className="flex items-baseline gap-2 px-4 pb-1 pt-3">
                <h3 className="text-sm font-medium">{t("sprint.removedFromSprint")}</h3>
                <span className="text-xs tabular-nums text-muted-foreground">{report.removedItems.length}</span>
              </div>
              <p className="px-4 pb-2 text-xs text-muted-foreground">{t("sprint.removedHint")}</p>
              <div className="flex flex-col px-2 pb-2">
                {report.removedItems.map((item) => (
                  <button
                    className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                    key={item.workObjectId}
                    onClick={() => onOpenItem?.(item.workObjectId)}
                    type="button"
                  >
                    <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" />
                    <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground line-through">{item.workObjectTitle}</span>
                    {item.typeName ? (
                      <Badge className="shrink-0 px-1.5 py-0 text-[0.65rem]" variant="secondary">
                        {item.typeName}
                      </Badge>
                    ) : null}
                    {item.removedAt ? (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t("sprint.removedAt", { date: formatRelativeTime(item.removedAt, t, locale) })}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      <EstimateSourcesDialog
        estimateSources={estimateSources}
        fields={fields}
        onAdd={sprints.addEstimateSource}
        onOpenChange={setIsSourcesOpen}
        onRemove={sprints.removeEstimateSource}
        onUpdate={sprints.updateEstimateSource}
        open={isSourcesOpen}
      />
    </div>
  );
}

function formatStayHours(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

const flowChartHeight = 120;

function FlowChart({ days }: { days: SprintReport["flow"] }) {
  const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
  const innerHeight = flowChartHeight - 24;
  const totals = days.map((day) => flowCategories.reduce((sum, category) => sum + (day.counts[category.key] ?? 0), 0));
  const maxTotal = Math.max(1, ...totals);
  const stepX = days.length <= 1 ? innerWidth : innerWidth / (days.length - 1);
  const x = (index: number) => chartPadding.left + index * stepX;
  const y = (value: number) => 8 + innerHeight * (1 - value / maxTotal);

  // Stack categories bottom-up in board order: closed at the base, to-do on top.
  const stackedOrder = [...flowCategories].reverse();
  const layers = stackedOrder.map((category, layerIndex) => {
    const lower = days.map((day) =>
      stackedOrder.slice(0, layerIndex).reduce((sum, other) => sum + (day.counts[other.key] ?? 0), 0),
    );
    const upper = days.map((day, dayIndex) => (lower[dayIndex] ?? 0) + (day.counts[category.key] ?? 0));
    const forward = upper.map((value, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(" ");
    const backward = [...lower.entries()]
      .reverse()
      .map(([index, value]) => `L${x(index).toFixed(1)},${y(value).toFixed(1)}`)
      .join(" ");
    return { color: category.color, key: category.key, path: `${forward} ${backward} Z` };
  });
  const labelEvery = Math.max(1, Math.ceil(days.length / 6));

  return (
    <svg className="mt-2 w-full" role="img" viewBox={`0 0 ${chartWidth} ${flowChartHeight}`}>
      {layers.map((layer) => (
        <path d={layer.path} fill={layer.color} fillOpacity={0.55} key={layer.key} stroke={layer.color} strokeWidth={1} />
      ))}
      {days.map((day, index) =>
        index % labelEvery === 0 || index === days.length - 1 ? (
          <text className="fill-muted-foreground text-[9px]" key={day.date} textAnchor="middle" x={x(index)} y={flowChartHeight - 2}>
            {day.date.slice(8, 10)}.{day.date.slice(5, 7)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

function SprintPicker({
  onSelect,
  selectedSprintId,
  sprints,
}: {
  onSelect: (sprintId: string) => void;
  selectedSprintId: string | null;
  sprints: Sprint[];
}) {
  const today = todayIso();
  const selected = sprints.find((sprint) => sprint.id === selectedSprintId) ?? null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button size="sm" variant="outline">
            <span className="max-w-40 truncate font-medium">{selected?.name ?? "Sprint"}</span>
            <ChevronDown data-icon="inline-end" className="text-muted-foreground" />
          </Button>
        }
      />
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuRadioGroup onValueChange={(value) => onSelect(String(value))} value={selectedSprintId ?? ""}>
          {[...sprints].reverse().map((sprint) => {
            const state = sprintStateFor(sprint, today);
            return (
              <DropdownMenuRadioItem key={sprint.id} value={sprint.id}>
                <span className={cn("size-1.5 shrink-0 rounded-full", sprintStateMeta[state].dotClassName)} />
                <span className="min-w-0 flex-1 truncate">{sprint.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatSprintRange(sprint.startsAt, sprint.endsAt)}</span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SprintHeadline({ sprint }: { sprint: Sprint }) {
  const t = useT();
  const state = sprintStateFor(sprint);
  const meta = sprintStateMeta[state];
  return (
    <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
      <span className={cn("rounded-full px-1.5 py-0.5 text-[0.65rem] font-medium", meta.badgeClassName)}>{t(meta.labelKey)}</span>
      <span>{formatSprintRange(sprint.startsAt, sprint.endsAt)}</span>
      {sprint.goal ? <span className="min-w-0 truncate">· {sprint.goal}</span> : null}
    </div>
  );
}

function AxisChip({ isActive, label, onClick }: { isActive: boolean; label: string; onClick: () => void }) {
  return (
    <button
      className={cn(
        "rounded px-2 py-1 text-xs transition-colors",
        isActive ? "bg-background font-medium text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
      )}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}

function StatCard({
  hint,
  label,
  tone,
  unit,
  value,
}: {
  hint?: string;
  label: string;
  tone?: "amber" | "emerald";
  unit?: string | null;
  value: number;
}) {
  return (
    <div className="rounded-lg border border-border/70 bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "emerald" && "text-emerald-600 dark:text-emerald-400",
          tone === "amber" && "text-amber-600 dark:text-amber-400",
        )}
      >
        {value}
        {unit ? <span className="ml-0.5 text-sm font-normal text-muted-foreground">{unit}</span> : null}
      </p>
      {hint ? <p className="mt-0.5 text-[0.7rem] text-muted-foreground/70">{hint}</p> : null}
    </div>
  );
}

function EstimateSourcesDialog({
  estimateSources,
  fields,
  onAdd,
  onOpenChange,
  onRemove,
  onUpdate,
  open,
}: {
  estimateSources: SprintEstimateSource[];
  fields: WorkObjectFieldDefinition[];
  onAdd: (input: { fieldDefinitionId: string; optionValues?: Record<string, number> | null }) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onRemove: (sourceId: string) => Promise<void>;
  onUpdate: (input: { optionValues: Record<string, number> | null; sourceId: string }) => Promise<void>;
  open: boolean;
}) {
  const t = useT();
  const usedFieldIds = new Set(estimateSources.map((source) => source.fieldDefinitionId));
  const candidates = fields.filter((field) => estimateFieldTypes.has(field.type) && !usedFieldIds.has(field.id));
  const fieldById = new Map(fields.map((field) => [field.id, field]));

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("sprint.estimateSources")}</DialogTitle>
          <DialogDescription>{t("sprint.estimateSourcesHint")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {estimateSources.map((source) => (
            <SourceRow
              field={fieldById.get(source.fieldDefinitionId) ?? null}
              key={source.id}
              onRemove={() => onRemove(source.id)}
              onUpdate={(optionValues) => onUpdate({ optionValues, sourceId: source.id })}
              source={source}
            />
          ))}
          {estimateSources.length === 0 ? (
            <p className="rounded-md bg-muted/40 px-3 py-4 text-center text-xs text-muted-foreground">
              {t("sprint.noSourcesYet")}
            </p>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button className="self-start" disabled={candidates.length === 0} size="sm" variant="outline">
                  <Plus data-icon="inline-start" />
                  {t("sprint.addSource")}
                </Button>
              }
            />
            <DropdownMenuContent align="start" className="w-64">
              {candidates.map((field) => (
                <DropdownMenuItem
                  key={field.id}
                  onClick={() => {
                    const optionValues =
                      field.type === "select"
                        ? Object.fromEntries(field.options.map((option, index) => [option.id, index + 1]))
                        : null;
                    void onAdd({ fieldDefinitionId: field.id, optionValues });
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{field.name}</span>
                  <span className="text-xs text-muted-foreground">{field.type}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SourceRow({
  field,
  onRemove,
  onUpdate,
  source,
}: {
  field: WorkObjectFieldDefinition | null;
  onRemove: () => void;
  onUpdate: (optionValues: Record<string, number> | null) => void;
  source: SprintEstimateSource;
}) {
  const t = useT();
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, number>>(() => source.optionValues ?? {});
  const isSelect = source.fieldType === "select" || source.fieldType === "multi_select";

  return (
    <div className="rounded-md border border-border/60">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{source.fieldName}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{source.fieldType === "duration" ? "hours" : source.fieldType}</span>
        {isSelect ? (
          <Button className="h-6 px-2 text-xs" onClick={() => setIsMappingOpen((current) => !current)} size="sm" variant="ghost">
            {isMappingOpen ? t("sprint.hideMapping") : t("sprint.mapping")}
          </Button>
        ) : null}
        <Button
          aria-label={t("a11y.remove", { name: source.fieldName })}
          className="size-6 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          size="icon-sm"
          variant="ghost"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      {isSelect && isMappingOpen ? (
        <div className="flex flex-col gap-1.5 px-3 pb-2.5">
          {(field?.options ?? []).map((option) => (
            <div className="flex items-center gap-2" key={option.id}>
              <span className="min-w-0 flex-1 truncate text-xs">{option.value}</span>
              <Input
                className="h-7 w-20 text-right text-xs"
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setDraft((current) => ({ ...current, [option.id]: Number.isFinite(next) ? next : 0 }));
                }}
                type="number"
                value={draft[option.id] ?? 0}
              />
            </div>
          ))}
          <Button className="self-end" onClick={() => onUpdate(draft)} size="sm" variant="outline">
            {t("sprint.saveMapping")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function percentOf(part: number, total: number) {
  if (total === 0) return "no items";
  return `${Math.round((part / total) * 100)}% of scope`;
}

const chartWidth = 560;
const chartHeight = 180;
const chartPadding = { bottom: 22, left: 28, right: 10, top: 10 };

function BurndownChart({ points }: { points: SprintReport["burndown"] }) {
  const t = useT();
  if (points.length === 0) {
    return <p className="mt-4 text-xs text-muted-foreground">{t("sprint.noData")}</p>;
  }
  const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
  const innerHeight = chartHeight - chartPadding.bottom - chartPadding.top;
  const maxValue = Math.max(1, ...points.map((point) => Math.max(point.ideal, point.remaining ?? 0)));
  const stepX = points.length <= 1 ? innerWidth : innerWidth / (points.length - 1);
  const x = (index: number) => chartPadding.left + index * stepX;
  const y = (value: number) => chartPadding.top + innerHeight * (1 - value / maxValue);

  const idealPath = points.map((point, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(point.ideal).toFixed(1)}`).join(" ");
  const actualPoints = points.filter((point) => point.remaining !== null);
  const actualPath = actualPoints
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(points.indexOf(point)).toFixed(1)},${y(point.remaining ?? 0).toFixed(1)}`)
    .join(" ");
  // Label roughly every fifth day to keep the axis quiet.
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  return (
    <svg className="mt-3 w-full" role="img" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
      {[...new Set([0, Math.round(maxValue / 2), maxValue])].map((value) => (
        <g key={value}>
          <line
            className="stroke-border/50"
            strokeDasharray={value === 0 ? undefined : "3 3"}
            x1={chartPadding.left}
            x2={chartWidth - chartPadding.right}
            y1={y(value)}
            y2={y(value)}
          />
          <text className="fill-muted-foreground text-[9px] tabular-nums" textAnchor="end" x={chartPadding.left - 5} y={y(value) + 3}>
            {value}
          </text>
        </g>
      ))}
      {points.map((point, index) =>
        index % labelEvery === 0 || index === points.length - 1 ? (
          <text
            className="fill-muted-foreground text-[9px]"
            key={point.date}
            textAnchor="middle"
            x={x(index)}
            y={chartHeight - 6}
          >
            {point.date.slice(8, 10)}.{point.date.slice(5, 7)}
          </text>
        ) : null,
      )}
      <path className="stroke-muted-foreground/50" d={idealPath} fill="none" strokeDasharray="4 4" strokeWidth={1.5} />
      {actualPoints.length > 0 ? (
        <>
          <path className="stroke-primary" d={actualPath} fill="none" strokeLinejoin="round" strokeWidth={2} />
          {(() => {
            const last = actualPoints[actualPoints.length - 1];
            if (!last) return null;
            return (
              <circle className="fill-primary" cx={x(points.indexOf(last))} cy={y(last.remaining ?? 0)} r={3} />
            );
          })()}
        </>
      ) : null}
    </svg>
  );
}

function VelocityChart({
  activeSprintId,
  entries,
  useEstimates = false,
}: {
  activeSprintId: string;
  entries: SprintReport["velocity"];
  useEstimates?: boolean;
}) {
  const t = useT();
  if (entries.length === 0) {
    return <p className="mt-3 text-xs text-muted-foreground">{t("sprint.closeFirst")}</p>;
  }
  const recent = entries.slice(-8);
  const doneOf = (entry: SprintReport["velocity"][number]) => (useEstimates ? entry.doneEstimate ?? 0 : entry.done);
  const totalOf = (entry: SprintReport["velocity"][number]) => (useEstimates ? entry.totalEstimate ?? 0 : entry.total);
  const max = Math.max(1, ...recent.map(totalOf));
  return (
    <div className="mt-3 flex h-28 items-end gap-2">
      {recent.map((entry) => (
        <div
          className="group/vbar flex min-w-0 flex-1 flex-col items-center gap-1"
          key={entry.sprintId}
          title={t("sprint.statusProgressHint", { done: doneOf(entry), name: entry.name, total: totalOf(entry) })}
        >
          <span className="text-[0.65rem] tabular-nums text-muted-foreground">{doneOf(entry)}</span>
          <div className="flex w-full max-w-10 flex-1 items-end justify-center">
            <div className="relative w-5 rounded-sm bg-muted/70" style={{ height: `${(totalOf(entry) / max) * 100}%` }}>
              <div
                className={cn(
                  "absolute inset-x-0 bottom-0 rounded-sm",
                  entry.sprintId === activeSprintId ? "bg-primary" : "bg-emerald-500/80",
                )}
                style={{ height: `${totalOf(entry) === 0 ? 0 : (doneOf(entry) / totalOf(entry)) * 100}%` }}
              />
            </div>
          </div>
          <span className="max-w-full truncate text-[0.65rem] text-muted-foreground">{entry.name}</span>
        </div>
      ))}
    </div>
  );
}
